// @file Helpers for dealing with emojis
import { __ } from '@@/bits/intl'
import { buildUrl } from '@@/bits/location'
import type {
  AccumulatedEmojiReactionData,
  AccumulatedNonEmojiReactionData,
  AccumulatedReactionData,
  Cid,
  EmojiPreset,
} from '@@/types'
import { EmojiVersion, ReactionType, type Emoji, type EmojiCategory } from '@padlet/arvo'
import emojiRegex from 'emoji-regex-xs'

interface EmojiPickerContext {
  type: 'comment' | 'post'
  postCid?: Cid
  anchorElement?: HTMLElement
  onEmojiSelection?: (unicode: string) => void
  isInExpandedView?: boolean
  darkMode?: boolean | null
  selectedEmojiUrl?: string
}

const emojiCategories = [
  'recents',
  'people',
  'nature',
  'food',
  'activity',
  'travel',
  'objects',
  'symbols',
  'flags',
] as const

type EmojiCategoryLabel = (typeof emojiCategories)[number]

const categoryTitles: Record<EmojiCategoryLabel, string> = {
  recents: __('Recents'),
  people: __('Smileys & People'),
  nature: __('Animals & Nature'),
  food: __('Food & Drinks'),
  activity: __('Activities'),
  travel: __('Travel & Places'),
  objects: __('Objects'),
  symbols: __('Symbols'),
  flags: __('Flags'),
}

const categoryIcons: Record<EmojiCategoryLabel, string> = {
  recents: 'recent_outline',
  people: 'smiley',
  nature: 'teddy',
  food: 'pizza',
  activity: 'ball',
  travel: 'car',
  objects: 'lightbulb',
  symbols: 'heart_outline',
  flags: 'flag_outline',
}

const categorySelectedIcons: Record<EmojiCategoryLabel, string> = {
  recents: 'recent_filled',
  people: 'smiley_filled',
  nature: 'teddy_filled',
  food: 'pizza_filled',
  activity: 'ball_filled',
  travel: 'car_filled',
  objects: 'lightbulb_filled',
  symbols: 'heart_filled',
  flags: 'flag_filled',
}

const OTHER_EMOJIS_CATEGORY: EmojiCategory = {
  label: 'other',
  title: __('Other'),
}

/**
 * Creates a unique key for an emoji based on its category and URL.
 * This ensures emojis in different categories (e.g., recents vs original category) are treated as distinct
 */
function getEmojiKey(emoji: Emoji): string {
  const categoryLabel = emoji.category?.label ?? OTHER_EMOJIS_CATEGORY.label
  return `${categoryLabel}:${emoji.url}`
}

// Joypixel emojis
const EMOJI_BASE_PATH = 'https://padlet.net/icons'
const EMOJI_VERSION = EmojiVersion.v8p0

/**
 * Composes a URL to an emoji.
 * @param type The type of the emoji (e.g. 'svg', 'png').
 * @param code A code that can be found in the emoji manifest.
 */
function emojiUrl(type: 'png' | 'svg', code: string): string {
  return buildUrl('https://padlet.net', `/icons/${EMOJI_VERSION}/${type}/${code}.${type}`)
}

/**
 * Composes a URL to an emoji with fixed ZWJ sequences.
 * This function ensures that emoji URLs use the correct ZWJ format for proper display.
 * @param type The type of the emoji (e.g. 'svg', 'png').
 * @param code A code that might be missing ZWJ sequences.
 * @returns A URL with the corrected emoji code including proper ZWJ sequences.
 */
function emojiUrlWithFixedCode(type: 'png' | 'svg', code: string): string {
  const fixedCode = fixEmojiCodeWithZWJ(code)
  return emojiUrl(type, fixedCode)
}

/**
 * Extracts emoji code from a Padlet emoji URL and converts it to Unicode
 * @param emojiUrl The emoji URL (e.g. "https://padlet.net/icons/v8.0/svg/1f644.svg")
 * @returns The Unicode character(s) representing the emoji, or empty string if invalid
 */
function emojiUrlToUnicode(emojiUrl: string): string {
  if (!emojiUrl || typeof emojiUrl !== 'string') {
    return ''
  }

  try {
    // Extract code from URL pattern: /icons/{version}/{type}/{code}.{extension}
    const match = emojiUrl.match(/\/icons\/[^/]+\/[^/]+\/([^.]+)\.(svg|png)$/)
    if (!match || !match[1]) {
      return ''
    }

    const code = match[1]
    return emojiCodeToUnicode(code)
  } catch {
    return ''
  }
}

/*
 * These are emojis that are ZWJ sequences but sometimes come without the ZWJ from emoji pickers
 * Format: 'emoji1-emoji2' -> 'emoji1-200d-emoji2-fe0f'
 */
const EMOJI_ZWJ_FIXES: Record<string, string> = {
  '1f636-1f32b': '1f636-200d-1f32b-fe0f', // 😶‍🌫️ face in clouds
  '1f635-1f4ab': '1f635-200d-1f4ab', // 😵‍💫 face with spiral eyes
  '1f62e-1f4a8': '1f62e-200d-1f4a8', // 😮‍💨 face exhaling
  '2764-1f525': '2764-fe0f-200d-1f525', // ❤️‍🔥 heart on fire
  '2764-1fa79': '2764-fe0f-200d-1fa79', // ❤️‍🩹 mending heart

  // Family emojis - man, woman, children combinations
  '1f468-1f469-1f467': '1f468-200d-1f469-200d-1f467', // 👨‍👩‍👧 family: man, woman, girl
  '1f468-1f469-1f466': '1f468-200d-1f469-200d-1f466', // 👨‍👩‍👦 family: man, woman, boy
  '1f468-1f469-1f467-1f466': '1f468-200d-1f469-200d-1f467-200d-1f466', // 👨‍👩‍👧‍👦 family: man, woman, girl, boy
  '1f468-1f469-1f466-1f466': '1f468-200d-1f469-200d-1f466-200d-1f466', // 👨‍👩‍👦‍👦 family: man, woman, boy, boy
  '1f468-1f469-1f467-1f467': '1f468-200d-1f469-200d-1f467-200d-1f467', // 👨‍👩‍👧‍👧 family: man, woman, girl, girl

  // Same-gender family combinations
  '1f468-1f468-1f466': '1f468-200d-1f468-200d-1f466', // 👨‍👨‍👦 family: man, man, boy
  '1f468-1f468-1f467': '1f468-200d-1f468-200d-1f467', // 👨‍👨‍👧 family: man, man, girl
  '1f468-1f468-1f467-1f466': '1f468-200d-1f468-200d-1f467-200d-1f466', // 👨‍👨‍👧‍👦 family: man, man, girl, boy
  '1f468-1f468-1f466-1f466': '1f468-200d-1f468-200d-1f466-200d-1f466', // 👨‍👨‍👦‍👦 family: man, man, boy, boy
  '1f468-1f468-1f467-1f467': '1f468-200d-1f468-200d-1f467-200d-1f467', // 👨‍👨‍👧‍👧 family: man, man, girl, girl

  '1f469-1f469-1f466': '1f469-200d-1f469-200d-1f466', // 👩‍👩‍👦 family: woman, woman, boy
  '1f469-1f469-1f467': '1f469-200d-1f469-200d-1f467', // 👩‍👩‍👧 family: woman, woman, girl
  '1f469-1f469-1f467-1f466': '1f469-200d-1f469-200d-1f467-200d-1f466', // 👩‍👩‍👧‍👦 family: woman, woman, girl, boy
  '1f469-1f469-1f466-1f466': '1f469-200d-1f469-200d-1f466-200d-1f466', // 👩‍👩‍👦‍👦 family: woman, woman, boy, boy
  '1f469-1f469-1f467-1f467': '1f469-200d-1f469-200d-1f467-200d-1f467', // 👩‍👩‍👧‍👧 family: woman, woman, girl, girl

  // Single parent families
  '1f468-1f466': '1f468-200d-1f466', // 👨‍👦 family: man, boy
  '1f468-1f467': '1f468-200d-1f467', // 👨‍👧 family: man, girl
  '1f468-1f466-1f466': '1f468-200d-1f466-200d-1f466', // 👨‍👦‍👦 family: man, boy, boy
  '1f468-1f467-1f466': '1f468-200d-1f467-200d-1f466', // 👨‍👧‍👦 family: man, girl, boy
  '1f468-1f467-1f467': '1f468-200d-1f467-200d-1f467', // 👨‍👧‍👧 family: man, girl, girl

  '1f469-1f466': '1f469-200d-1f466', // 👩‍👦 family: woman, boy
  '1f469-1f467': '1f469-200d-1f467', // 👩‍👧 family: woman, girl
  '1f469-1f466-1f466': '1f469-200d-1f466-200d-1f466', // 👩‍👦‍👦 family: woman, boy, boy
  '1f469-1f467-1f466': '1f469-200d-1f467-200d-1f466', // 👩‍👧‍👦 family: woman, girl, boy
  '1f469-1f467-1f467': '1f469-200d-1f467-200d-1f467', // 👩‍👧‍👧 family: woman, girl, girl

  // Couple emojis
  '1f468-1f469': '1f468-200d-1f469', // 👨‍👩 couple: man, woman
  '1f468-1f468': '1f468-200d-1f468', // 👨‍👨 couple: man, man
  '1f469-1f469': '1f469-200d-1f469', // 👩‍👩 couple: woman, woman

  // Professional role emojis with ZWJ
  '1f469-1f4bb': '1f469-200d-1f4bb', // 👩‍💻 woman technologist
  '1f468-1f4bb': '1f468-200d-1f4bb', // 👨‍💻 man technologist
  '1f469-1f3a8': '1f469-200d-1f3a8', // 👩‍🎨 woman artist
  '1f468-1f3a8': '1f468-200d-1f3a8', // 👨‍🎨 man artist
  '1f469-1f373': '1f469-200d-1f373', // 👩‍🍳 woman cook
  '1f468-1f373': '1f468-200d-1f373', // 👨‍🍳 man cook
  '1f469-1f527': '1f469-200d-1f527', // 👩‍🔧 woman mechanic
  '1f468-1f527': '1f468-200d-1f527', // 👨‍🔧 man mechanic
  '1f469-1f692': '1f469-200d-1f692', // 👩‍🚒 woman firefighter
  '1f468-1f692': '1f468-200d-1f692', // 👨‍🚒 man firefighter

  // Flag emojis with ZWJ
  '1f3f3-1f308': '1f3f3-fe0f-200d-1f308', // 🏳️‍🌈 rainbow flag
  '1f3f3-26a7': '1f3f3-fe0f-200d-26a7-fe0f', // 🏳️‍⚧️ transgender flag
  '1f3f4-2620': '1f3f4-200d-2620-fe0f', // 🏴‍☠️ pirate flag
}

const RED_HEART_EMOJI_STRING = String.fromCodePoint(0x2764, 0xfe0f)
const PLAIN_HEART_EMOJI_STRING = String.fromCodePoint(0x2764)
const CUSTOM_EMOJI_STRINGS = [RED_HEART_EMOJI_STRING, PLAIN_HEART_EMOJI_STRING]

/**
 * Fixes emoji codes that are missing ZWJ for known problematic emojis
 * @param code The emoji hex code string that might be missing ZWJ
 * @returns The fixed emoji code with ZWJ inserted if needed
 */
function fixEmojiCodeWithZWJ(code: string): string {
  // Check if this is a known emoji that needs ZWJ
  const baseCode = code.replace(/-fe0f$/i, '') // Remove variation selector if present
  if (EMOJI_ZWJ_FIXES[baseCode.toLowerCase()]) {
    return EMOJI_ZWJ_FIXES[baseCode.toLowerCase()]
  }
  return code
}

/**
 * Properly segments text into grapheme clusters (including complex emojis like flags and ZWJ sequences)
 * Uses Intl.Segmenter for proper Unicode handling when available, falls back to Array.from
 * @param text The text to segment
 * @returns Array of grapheme clusters
 */
function segmentText(text: string): string[] {
  if (typeof Intl !== 'undefined' && Intl.Segmenter != null) {
    const segmenter = new Intl.Segmenter('en', { granularity: 'grapheme' })
    return Array.from(segmenter.segment(text), (s) => s.segment)
  }
  // Fallback for older browsers
  return Array.from(text)
}

/**
 * Converts an emoji hex code string to its Unicode character representation
 * @param code The emoji hex code string (e.g. "1f645" or "1f645-1f3fb-2642")
 * @returns The Unicode character(s) representing the emoji, or empty string if invalid
 */
function emojiCodeToUnicode(code: string): string {
  if (!code || typeof code !== 'string') {
    return ''
  }

  // Reject codes that start or end with dashes or contain consecutive dashes
  if (code.startsWith('-') || code.endsWith('-') || code.includes('--')) {
    return ''
  }

  // Special case for red heart emoji: convert "2764" to ❤️ (with variation selector-16)
  if (code === RED_HEART_EMOJI.code) {
    return RED_HEART_EMOJI_STRING
  }

  // Fix known emoji codes that are missing ZWJ
  const fixedCode = fixEmojiCodeWithZWJ(code)
  const parts = fixedCode.split('-')

  // Validate that all parts are valid hex codes
  for (const part of parts) {
    // Check if part is valid hex and has appropriate length for emoji codes
    // Emoji hex codes are typically 3-6 characters long (allow 3 for some special codes like 200d)
    if (!/^[0-9a-fA-F]{3,6}$/.test(part)) {
      return ''
    }

    // Additional check: reject simple numbers like "1", "2", etc.
    // For short codes (3 chars), be more restrictive - only allow known special codes
    // For 4+ char codes, allow anything above 0x1000 to include common symbols
    const hexValue = parseInt(part, 16)
    if (part.length === 3) {
      // Only allow specific 3-char codes like 200d (zero-width joiner)
      if (hexValue !== 0x200d) {
        return ''
      }
    } else if (hexValue < 0x1000) {
      return ''
    }
  }

  try {
    return parts
      .map((part, index) => {
        const hexValue = parseInt(part, 16)
        // Add variation selector-16 (0xFE0F) for red heart emoji
        // But only if the next part is NOT already fe0f (to avoid double variation selectors)
        if (part.toLowerCase() === RED_HEART_EMOJI.code.toLowerCase() && parts[index + 1]?.toLowerCase() !== 'fe0f') {
          return RED_HEART_EMOJI_STRING
        }
        return String.fromCodePoint(hexValue)
      })
      .join('')
  } catch {
    // Catch RangeError from String.fromCodePoint() for:
    // - Code points > 0x10FFFF (max Unicode)
    // - Surrogate code points (0xD800-0xDFFF) - reserved for UTF-16
    // - Invalid code points that passed validation but are still invalid
    return ''
  }
}

/**
 * Checks if a text segment is a complex emoji character using Unicode properties
 * This handles compound emojis, ZWJ sequences, and other emoji patterns that might not be in our database
 * @param text The text segment to check
 * @returns True if the text appears to be an emoji character
 */
function isEmojiText(text: string): boolean {
  if (!text || text.length === 0) {
    return false
  }

  // Create a new regex instance each time to avoid stateful regex issues with the 'g' flag
  const regex = emojiRegex()
  return regex.test(text) || CUSTOM_EMOJI_STRINGS.some((customEmoji) => customEmoji === text)
}

// #region Common emojis used in the app
type CommonEmoji = Pick<Emoji, 'code' | 'name'> & { iconSrc: Emoji['url'] }

// Alert icon is different in that it doesn't come from joypixels -- custom made from figma
// See https://linear.app/padlet/issue/PRO-28463/change-warning-logo#comment-76790419
const ALERT_SIGN_EMOJI: CommonEmoji = {
  code: '26a0',
  name: __('alert sign'),
  iconSrc: buildUrl('https://padlet.net', `/icons/svg/v1/caution.svg`),
}

const ANXIOUS_FACE_EMOJI: CommonEmoji = {
  code: '1f630',
  name: __('anxious face'),
  iconSrc: emojiUrl('svg', '1f630'),
}

const ARMS_CROSSED_IN_X_EMOJI: CommonEmoji = {
  code: '1f645-1f3fb-2642',
  name: __('arms crossed in x'),
  iconSrc: emojiUrl('svg', '1f645-1f3fb-2642'),
}

const COLD_FACE_EMOJI: CommonEmoji = {
  code: '1f976',
  name: __('cold face'),
  iconSrc: emojiUrl('svg', '1f976'),
}

const FACE_WITH_ROLLING_EYES_EMOJI: CommonEmoji = {
  code: '1f644',
  name: __('face with rolling eyes'),
  iconSrc: emojiUrl('svg', '1f644'),
}

const WAVING_HAND_EMOJI: CommonEmoji = {
  code: '1f44b',
  name: __('waving hand'),
  iconSrc: emojiUrl('svg', '1f44b'),
}

const LIGHT_BULB_EMOJI: CommonEmoji = {
  code: '1f4a1',
  name: __('light bulb'),
  iconSrc: emojiUrl('svg', '1f4a1'),
}

const OPEN_HANDS_ICON: CommonEmoji = {
  code: '1f450',
  name: __('open hands'),
  iconSrc: emojiUrl('svg', '1f450'),
}

const RED_HEART_EMOJI: CommonEmoji = {
  code: '2764',
  name: __('red heart'),
  iconSrc: emojiUrl('svg', '2764'),
}

const PARACHUTE_EMOJI: CommonEmoji = {
  code: '1fa82',
  name: __('parachute'),
  iconSrc: emojiUrl('svg', '1fa82'),
}

const PARTY_POPPER_EMOJI: CommonEmoji = {
  code: '1f389',
  name: __('party popper'),
  iconSrc: emojiUrl('svg', '1f389'),
}

const ROBOT_FACE_EMOJI: CommonEmoji = {
  code: '1f916',
  name: __('robot face'),
  iconSrc: emojiUrl('png', '1f916'),
}

const ROCKET_EMOJI: CommonEmoji = {
  code: '1f680',
  name: __('rocket'),
  iconSrc: emojiUrl('png', '1f680'),
}

const HANDSHAKE_EMOJI: CommonEmoji = {
  code: '1f91d',
  name: __('handshake'),
  iconSrc: emojiUrl('svg', '1f91d'),
}

const STOP_HAND_EMOJI: CommonEmoji = {
  code: '270b',
  name: __('stop hand'),
  iconSrc: emojiUrl('svg', '270b'),
}

const INCOMING_ENVELOPE_EMOJI: CommonEmoji = {
  code: '1f4e8',
  name: __('incoming envelope'),
  iconSrc: emojiUrl('png', '1f4e8'),
}

const THINKING_FACE_EMOJI: CommonEmoji = {
  code: '1f914',
  name: __('thinking face'),
  iconSrc: emojiUrl('png', '1f914'),
}

const SQUINTING_LAUGHING_FACE_EMOJI: CommonEmoji = {
  code: '1f606',
  name: __('squinting laughing face'),
  iconSrc: emojiUrl('svg', '1f606'),
}

const THUMBS_UP_EMOJI: CommonEmoji = {
  code: '1f44d',
  name: __('thumbs up'),
  iconSrc: emojiUrl('svg', '1f44d'),
}
// #endregion

function isEmojiReactionData(data: AccumulatedReactionData): data is AccumulatedEmojiReactionData {
  return data.reactionType === ReactionType.Emoji
}

function isNonEmojiReactionData(data: AccumulatedReactionData): data is AccumulatedNonEmojiReactionData {
  return data.reactionType !== ReactionType.Emoji
}

/** Map of preset to allowed_emojis (matching backend's EmojiValidationService.wall_reaction_data_from_preset) */
const PRESET_ALLOWED_EMOJIS: Record<EmojiPreset, string[] | null> = {
  all: null, // null or empty array means all emojis allowed
  classic: ['2764', '1f389', '1f606', '1f44d'],
  heart: ['2764'],
  thumbs_up: ['1f44d'],
  off: [],
}

export {
  ALERT_SIGN_EMOJI,
  ANXIOUS_FACE_EMOJI,
  ARMS_CROSSED_IN_X_EMOJI,
  categoryIcons,
  categorySelectedIcons,
  categoryTitles,
  COLD_FACE_EMOJI,
  EMOJI_BASE_PATH,
  EMOJI_VERSION,
  emojiCategories,
  emojiCodeToUnicode,
  emojiUrl,
  emojiUrlToUnicode,
  emojiUrlWithFixedCode,
  FACE_WITH_ROLLING_EYES_EMOJI,
  getEmojiKey,
  HANDSHAKE_EMOJI,
  INCOMING_ENVELOPE_EMOJI,
  isEmojiReactionData,
  isEmojiText,
  isNonEmojiReactionData,
  LIGHT_BULB_EMOJI,
  OPEN_HANDS_ICON,
  OTHER_EMOJIS_CATEGORY,
  PARACHUTE_EMOJI,
  PARTY_POPPER_EMOJI,
  PLAIN_HEART_EMOJI_STRING,
  PRESET_ALLOWED_EMOJIS,
  RED_HEART_EMOJI,
  RED_HEART_EMOJI_STRING,
  ROBOT_FACE_EMOJI,
  ROCKET_EMOJI,
  segmentText,
  SQUINTING_LAUGHING_FACE_EMOJI,
  STOP_HAND_EMOJI,
  THINKING_FACE_EMOJI,
  THUMBS_UP_EMOJI,
  WAVING_HAND_EMOJI,
}
export type { CommonEmoji, EmojiPickerContext }
